import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { parseQueryParams } from '../../utils';
import { baseQueryWithReauth } from './baseQuery';

export enum SalesOrderTableConfigPage {
  LISTING = 'LISTING',
  APPROVALS = 'APPROVALS',
  REPORTS = 'REPORTS',
  LINE_ITEM_REPORTS = 'LINE_ITEM_REPORTS',
}

export interface SalesOrderColumnConfigItem {
  key: string;
  label: string;
  visible: boolean;
  order: number;
}

export interface SalesOrderFieldConfigDto {
  columns: SalesOrderColumnConfigItem[];
}

export interface CreateSalesOrderTableConfigDto {
  page: SalesOrderTableConfigPage;
  name: string;
  fieldConfigJson: {
    data: SalesOrderColumnConfigItem[];
  };
}

export interface UpdateSalesOrderTableConfigDto {
  page: SalesOrderTableConfigPage;
  name: string;
  fieldConfigJson: {
    data: SalesOrderColumnConfigItem[];
  };
}

export interface ApplySalesOrderTableConfigDto {
  configId: number;
  page: SalesOrderTableConfigPage;
}

export interface SalesOrderTableConfig {
  id: number;
  page?: SalesOrderTableConfigPage;
  name: string;
  configName?: string; // For backward compatibility
  fieldConfigJson?: {
    data: SalesOrderColumnConfigItem[];
  };
  columns?: SalesOrderColumnConfigItem[]; // For backward compatibility
  data?: SalesOrderColumnConfigItem[];
  isDefault?: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdByUser?: {
    ID: number;
    FullName: string;
    EmailID: string;
  };
}

function parseSalesOrderFieldConfigJson(
  fieldConfigJson: unknown,
): { data: SalesOrderColumnConfigItem[] } | undefined {
  if (fieldConfigJson == null) return undefined;

  let parsed: unknown = fieldConfigJson;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return undefined;
    }
  }

  if (Array.isArray(parsed)) {
    return { data: parsed as SalesOrderColumnConfigItem[] };
  }

  if (
    parsed &&
    typeof parsed === 'object' &&
    Array.isArray((parsed as { data?: unknown }).data)
  ) {
    return parsed as { data: SalesOrderColumnConfigItem[] };
  }

  return undefined;
}

export function getSalesOrderTableConfigColumns(
  config?: SalesOrderTableConfig | null,
): SalesOrderColumnConfigItem[] {
  if (!config) return [];

  const fieldConfig =
    parseSalesOrderFieldConfigJson(config.fieldConfigJson) ||
    (Array.isArray(config.columns) ? { data: config.columns } : undefined) ||
    (Array.isArray(config.data) ? { data: config.data } : undefined);

  return [...(fieldConfig?.data ?? [])]
    .filter(column => column.visible !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

export function extractSalesOrderActiveConfigId(raw: unknown): number | undefined {
  if (!raw || typeof raw !== 'object') return undefined;

  const record = raw as Record<string, unknown>;
  if (typeof record.configId === 'number') return record.configId;
  if (typeof record.tableConfigId === 'number') return record.tableConfigId;

  if (record.config && typeof record.config === 'object') {
    const config = record.config as Record<string, unknown>;
    if (typeof config.id === 'number') return config.id;
  }

  if (typeof record.id === 'number') return record.id;
  return undefined;
}

export function normalizeSalesOrderTableConfig(
  raw: unknown,
): SalesOrderTableConfig | null {
  if (!raw || typeof raw !== 'object') return null;

  const record = raw as Record<string, unknown>;
  const nested =
    record.config && typeof record.config === 'object'
      ? (record.config as SalesOrderTableConfig)
      : (raw as SalesOrderTableConfig);

  const fieldConfigJson =
    parseSalesOrderFieldConfigJson(nested.fieldConfigJson) ||
    (Array.isArray(nested.columns)
      ? { data: nested.columns }
      : undefined) ||
    (Array.isArray(nested.data) ? { data: nested.data } : undefined);

  if (!fieldConfigJson?.data?.length) {
    const id = extractSalesOrderActiveConfigId(raw);
    if (id) {
      return { ...nested, id };
    }
    return null;
  }

  return {
    ...nested,
    fieldConfigJson,
    columns: nested.columns ?? fieldConfigJson.data,
  };
}

const salesOrderTableConfigApi = createApi({
  reducerPath: 'salesOrderTableConfig',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['SalesOrderTableConfig'],
  endpoints: builder => ({
    // Get all table configurations
    getSalesOrderTableConfigList: builder.query<
      BackendResponse<SalesOrderTableConfig[]>,
      void
    >({
      query: () => ({
        url: '/sales-order-table-config',
        method: 'GET',
      }),
      providesTags: ['SalesOrderTableConfig'],
    }),

    // Get active table configuration
    getActiveSalesOrderTableConfig: builder.query<
      BackendResponse<SalesOrderTableConfig>,
      { page: SalesOrderTableConfigPage }
    >({
      query: ({ page }) => ({
        url: `/sales-order-table-config/active${parseQueryParams({ page })}`,
        method: 'GET',
      }),
      providesTags: ['SalesOrderTableConfig'],
    }),

    // Get table configuration by ID
    getSalesOrderTableConfig: builder.query<
      BackendResponse<SalesOrderTableConfig>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/sales-order-table-config/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, { id }) => [
        { type: 'SalesOrderTableConfig', id },
      ],
    }),

    // Create table configuration
    createSalesOrderTableConfig: builder.mutation<
      BackendResponse<SalesOrderTableConfig>,
      CreateSalesOrderTableConfigDto
    >({
      query: payload => ({
        url: '/sales-order-table-config',
        method: 'POST',
        body: payload,
      }),
    }),

    // Update table configuration
    updateSalesOrderTableConfig: builder.mutation<
      BackendResponse<SalesOrderTableConfig>,
      { id: number; data: UpdateSalesOrderTableConfigDto }
    >({
      query: ({ id, data }) => ({
        url: `/sales-order-table-config/${id}`,
        method: 'PUT',
        body: data,
      }),
    }),

    // Delete table configuration
    deleteSalesOrderTableConfig: builder.mutation<
      BackendResponse<void>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/sales-order-table-config/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SalesOrderTableConfig'],
    }),

    // Apply table configuration
    applySalesOrderTableConfig: builder.mutation<
      BackendResponse<void>,
      ApplySalesOrderTableConfigDto
    >({
      query: payload => ({
        url: '/sales-order-table-config/apply',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['SalesOrderTableConfig'],
    }),
  }),
});

export const {
  useGetSalesOrderTableConfigListQuery,
  useGetActiveSalesOrderTableConfigQuery,
  useGetSalesOrderTableConfigQuery,
  useLazyGetSalesOrderTableConfigQuery,
  useCreateSalesOrderTableConfigMutation,
  useUpdateSalesOrderTableConfigMutation,
  useDeleteSalesOrderTableConfigMutation,
  useApplySalesOrderTableConfigMutation,
} = salesOrderTableConfigApi;

export default salesOrderTableConfigApi;
